/**
 * Предустановка: Сценарий для работы с remote репозиториями
 * Используется для уроков по работе с удалёнными репозиториями
 */

import type { GitPreset } from './types';

/** Проект для демонстрации работы с remote репозиториями */
export const remoteSyncPreset: GitPreset = {
  id: 'remote-sync',
  name: 'Remote репозиторий',
  description: 'Проект с remote репозиторием для изучения fetch, pull, push',

  currentBranch: 'main',

  branches: [
    { name: 'main', head: 'c3', color: '#F05032' },
    { name: 'feature/remote-feature', head: 'c5', color: '#4a9eff' },
    // Remote branches
    { name: 'origin/main', head: 'c4', color: '#F05032', isRemote: true, remoteName: 'origin' },
    {
      name: 'origin/feature/remote-feature',
      head: 'c5',
      color: '#4a9eff',
      isRemote: true,
      remoteName: 'origin',
    },
  ],

  commits: [
    // Локальные коммиты
    {
      sha: 'c1',
      message: 'Initial commit: Project setup',
      parents: [],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704067200, // 2024-01-01
      files: ['README.md', 'package.json'],
    },
    {
      sha: 'c2',
      message: 'Add core functionality',
      parents: ['c1'],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704153600, // 2024-01-02
      files: ['src/index.ts', 'src/core.ts'],
    },
    {
      sha: 'c3',
      message: 'Update README with installation instructions',
      parents: ['c2'],
      author: 'Ivan Petrov',
      authorEmail: 'ivan@example.com',
      timestamp: 1704240000, // 2024-01-03
      files: ['README.md'],
    },
    // Remote коммит (есть на origin, но нет локально)
    {
      sha: 'c4',
      message: 'Add contributing guidelines (from remote)',
      parents: ['c2'],
      author: 'Maria Ivanova',
      authorEmail: 'maria@example.com',
      timestamp: 1704240000, // 2024-01-03 (параллельно с c3)
      files: ['CONTRIBUTING.md', 'docs/SETUP.md'],
    },
    // Feature branch (синхронизирована с remote)
    {
      sha: 'c5',
      message: 'Implement remote feature',
      parents: ['c4'],
      author: 'Maria Ivanova',
      authorEmail: 'maria@example.com',
      timestamp: 1704326400, // 2024-01-04
      files: ['src/remote-feature.ts'],
    },
  ],

  remotes: [
    {
      name: 'origin',
      url: 'https://github.com/example/project.git',
      branches: {
        main: 'c4',
        'feature/remote-feature': 'c5',
      },
    },
  ],

  files: [
    {
      path: 'README.md',
      content: `# Remote Project

Проект для демонстрации работы с remote репозиториями.

## Установка

\`\`\`bash
git clone https://github.com/example/project.git
cd project
npm install
\`\`\`

## Разработка

\`\`\`bash
npm run dev
\`\`\`

## Сборка

\`\`\`bash
npm run build
\`\`\`
`,
      status: 'committed',
    },
    {
      path: 'package.json',
      content: `{
  "name": "remote-project",
  "version": "1.0.0",
  "description": "Project for remote repository demonstration",
  "main": "src/index.ts",
  "scripts": {
    "dev": "ts-node src/index.ts",
    "build": "tsc",
    "test": "jest"
  },
  "repository": {
    "type": "git",
    "url": "https://github.com/example/project.git"
  },
  "author": "Ivan Petrov",
  "license": "MIT"
}
`,
      status: 'committed',
    },
    {
      path: 'src/index.ts',
      content: `import { Core } from './core';

const app = new Core();

app.initialize().then(() => {
  console.log('Application initialized');
}).catch((error) => {
  console.error('Initialization failed:', error);
});
`,
      status: 'committed',
    },
    {
      path: 'src/core.ts',
      content: `export class Core {
  private initialized = false;

  async initialize(): Promise<void> {
    if (this.initialized) {
      throw new Error('Already initialized');
    }

    await this.loadConfig();
    this.initialized = true;
  }

  private async loadConfig(): Promise<void> {
    // Load configuration
    console.log('Loading configuration...');
  }

  async shutdown(): Promise<void> {
    this.initialized = false;
    console.log('Shutdown complete');
  }
}
`,
      status: 'committed',
    },
    {
      path: 'CONTRIBUTING.md',
      content: `# Contributing Guidelines

Спасибо за интерес к проекту!

## Как внести вклад

1. Сделайте fork репозитория
2. Создайте feature ветку (\`git checkout -b feature/amazing-feature\`)
3. Зафиксируйте изменения (\`git commit -m 'Add amazing feature'\`)
4. Отправьте в ветку (\`git push origin feature/amazing-feature\`)
5. Откройте Pull Request

## Стандарты кода

- Используйте TypeScript
- Следуйте ESLint правилам
- Пишите тесты для нового кода
- Обновляйте документацию

## Отчёты об ошибках

Используйте GitHub Issues для сообщений об ошибках.
`,
      status: 'committed',
    },
    {
      path: 'docs/SETUP.md',
      content: `# Setup Guide

## Требования

- Node.js 18+
- npm или pnpm
- Git

## Установка

1. Клонируйте репозиторий:
\`\`\`bash
git clone https://github.com/example/project.git
cd project
\`\`\`

2. Установите зависимости:
\`\`\`bash
npm install
\`\`\`

3. Запустите проект:
\`\`\`bash
npm run dev
\`\`\`

## Переменные окружения

Создайте \`.env\` файл:

\`\`\`
NODE_ENV=development
API_URL=https://api.example.com
DEBUG=true
\`\`\`
`,
      status: 'committed',
    },
    {
      path: 'src/remote-feature.ts',
      content: `/**
 * Remote feature implementation
 * Добавлена из remote репозитория
 */

export interface RemoteConfig {
  endpoint: string;
  timeout: number;
  retries: number;
}

export class RemoteFeature {
  constructor(private config: RemoteConfig) {}

  async connect(): Promise<boolean> {
    try {
      const response = await fetch(this.config.endpoint, {
        method: 'HEAD',
        signal: AbortSignal.timeout(this.config.timeout),
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  async fetchData<T>(path: string): Promise<T> {
    const url = \`\${this.config.endpoint}\${path}\`;

    for (let attempt = 0; attempt < this.config.retries; attempt++) {
      try {
        const response = await fetch(url);
        if (response.ok) {
          return response.json();
        }
      } catch (error) {
        if (attempt === this.config.retries - 1) {
          throw error;
        }
        await this.delay(1000 * (attempt + 1));
      }
    }

    throw new Error('Failed to fetch data');
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
`,
      status: 'committed',
    },
  ],

  // Рабочая директория с изменениями (для демонстрации push)
  workingDir: [
    {
      path: 'src/core.ts',
      status: 'modified',
    },
  ],

  visualization: {
    branchColors: {
      main: '#F05032',
      'feature/remote-feature': '#4a9eff',
      'origin/main': '#F05032',
      'origin/feature/remote-feature': '#4a9eff',
    },
    nodePositions: {
      c1: { x: 100, y: 200 },
      c2: { x: 200, y: 200 },
      c3: { x: 300, y: 150 }, // Локальный main
      c4: { x: 300, y: 250 }, // Remote main
      c5: { x: 400, y: 250 }, // Feature (синхронизирована)
    },
    annotations: [
      { sha: 'c3', text: 'Локальный main (нужно push)', position: 'top' },
      { sha: 'c4', text: 'origin/main (нужно pull)', position: 'bottom' },
      { sha: 'c5', text: 'Feature синхронизирована', position: 'right' },
    ],
    highlightedCommits: ['c3', 'c4'],
  },
};

/**
 * Состояние после синхронизации
 */
export const afterSyncState = {
  description: 'После git pull и git push, локальный и remote main синхронизированы',
  mergeCommit: {
    sha: 'c6',
    message: 'Merge remote changes',
    parents: ['c3', 'c4'],
    author: 'Ivan Petrov',
    authorEmail: 'ivan@example.com',
    timestamp: 1704412800,
  },
  visualization: {
    nodePositions: {
      c1: { x: 100, y: 200 },
      c2: { x: 200, y: 200 },
      c3: { x: 300, y: 150 },
      c4: { x: 300, y: 250 },
      c5: { x: 400, y: 250 },
      c6: { x: 400, y: 200 }, // Merge commit
    },
    annotations: [{ sha: 'c6', text: 'Синхронизировано', position: 'top' }],
  },
};
